"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import type { CatalogItem } from "@/lib/airtable";
import { buildPieceHref } from "@/lib/pieceNavigation";

// Use an explicit locale so server (Node) and client (browser) produce
// identical formatted strings, avoiding hydration mismatches.
const formatPrice = (n: number) => new Intl.NumberFormat("en-US").format(n);

type Props = {
  items: CatalogItem[];
};

export default function NewArrivalsClient({ items }: Props) {
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});

  const handleImageLoaded = (id: string) => {
    setLoadedImages((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
  };

  return (
    <div className="px-4 py-12">
      <div className="mb-12">
        <p className="text-xs uppercase tracking-[0.3em] text-slate-500 mb-3">New Pieces</p>
        <h2 className="text-2xl md:text-3xl font-semibold uppercase tracking-[0.2em] text-slate-900 font-[var(--font-display)]">
          Recently Added
        </h2>
      </div>

      {items.length === 0 ? (
        <div className="glass my-10 mx-auto max-w-lg space-y-4 rounded-3xl border border-slate-200 bg-white p-10 text-center text-slate-900 shadow-soft">
          <h2 className="text-2xl font-semibold">No pieces available right now</h2>
          <p className="text-slate-600">Check back soon for new arrivals!</p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 pb-12">
          {items.map((item) => (
            <Link
              key={item.id}
              href={
                buildPieceHref(item.id, { backHref: "/new-arrivals", backLabel: "New Arrivals" }) as Parameters<
                  typeof Link
                >[0]["href"]
              }
              className="group block"
            >
              <article className="bg-white p-4 text-left transition group-hover:-translate-y-1">
                <div className="relative aspect-[4/3] overflow-hidden bg-white">
                  {item.soldOut && (
                    <div className="absolute left-3 top-3 z-10 rounded-sm bg-slate-500/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-white">
                      Sold out
                    </div>
                  )}
                  {item.pictureUrl ? (
                    <>
                      {!loadedImages[item.id] && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white">
                          <Image src="/tube-spinner-grey.svg" alt="" width={48} height={48} />
                        </div>
                      )}
                      <Image
                        src={item.pictureUrl}
                        alt={item.itemName || "Jewelry item"}
                        fill
                        className="object-contain"
                        sizes="(max-width: 768px) 90vw, (max-width: 1024px) 45vw, 320px"
                        onLoadingComplete={() => handleImageLoaded(item.id)}
                      />
                    </>
                  ) : (
                    <div className="flex h-full items-center justify-center text-slate-400">Image coming soon</div>
                  )}
                </div>
              </article>
              <div className="flex items-baseline justify-between gap-4">
                <h2 className="text-med text-slate-600">{item.itemName || "Untitled"}</h2>
                <span className="text-xs text-slate-900">
                  {item.tagPrice ? `$${formatPrice(item.tagPrice)}` : "Request"}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
